'use client';

import Image from '@repo/ui/src/components/Image';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Button, { ButtonVariant } from '../Button';
import KnifeIcon from '@repo/ui/src/components/Icons/KnifeIcon';
import { GridLearnItem } from '@repo/ui/src/types/grid';
import buildImageSrc from '../../functions/image-functions-aspect-ratio';
import { classNames } from '../../utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useId } from 'react';

type BackgroundStyle = 'Light' | 'Dark';

interface LearnCardProps extends GridLearnItem {
  backgroundStyle?: BackgroundStyle;
}

const LearnCard = ({ _meta, title, headerAsset, seo, backgroundStyle }: LearnCardProps) => {
  const isDark = backgroundStyle === 'Dark';
  const imageUrl = buildImageSrc(headerAsset?.length ? headerAsset[0] : null, 700, 2, 1);
  const link = _meta?.deliveryKey || '';
  const description = seo?.ogDescription;
  const titleId = `learn-card-title${useId()}`;
  const { t } = useTranslation();

  return (
    <article
      aria-labelledby={titleId}
      className={classNames(
        'relative h-full flex flex-col group border',
        isDark && 'bg-neutral-700 text-white border-neutral-500',
      )}
    >
      <Button
        link={link}
        as={Anchor}
        variant={ButtonVariant.bare}
        className="w-full flex-grow flex flex-col"
        size="none"
        ariaLabelledBy={titleId}
      >
        <div className={classNames('relative w-full overflow-hidden aspect-video')}>
          {imageUrl && (
            <Image
              src={imageUrl}
              alt={title || ''}
              layout="fill"
              objectFit="cover"
              className="group-hover:scale-105 transition-transform duration-1000"
              data-testid="learn-card-image"
            />
          )}
        </div>

        <div className="flex flex-col flex-grow justify-between p-4">
          <div className="space-y-3">
            <h3
              id={titleId}
              className={classNames('tra-text-web-sub-2', isDark && 'text-neutral-200')}
            >
              {title}
            </h3>

            {description && (
              <p
                className={classNames(
                  'tra-text-web-body-m normal-case line-clamp-2 mb-0',
                  isDark ? 'text-white' : 'text-neutral-800',
                )}
              >
                {description}
              </p>
            )}
          </div>

          <div
            aria-hidden="true"
            className="tra-btn-special tra-text-web-utl-button-s pt-4 flex w-full items-center justify-between tracking-normal group-hover:text-brand-300 transition-colors duration-300"
          >
            {t('learnCard.readMore', undefined, {
              default: 'Read More',
            })}
            <KnifeIcon
              className={classNames(
                'tra-btn-icon-special group-hover:text-brand-300',
                isDark ? 'text-white' : 'text-neutral-800',
              )}
              aria-hidden="true"
            />
          </div>
        </div>
      </Button>
    </article>
  );
};

export default LearnCard;
